const __vite__mapDeps=(i,m=__vite__mapDeps,d=(m.f||(m.f=["ui/date-picker-field/calendar/calendar-wut.js","ui/date-picker-field/calendar/calendar.js","contexts/config-context.js","contexts/translation-context.js","date-localization/get-first-day-of-the-week.js","field-settings/settings-shared/availability/availability.js","date-localization/local-date.js","field-settings/settings-shared/first-day-of-week-constants.js","ui/date-picker-field/date-picker-input/date-picker-utils.js","ui/date-picker-field/wix-design-systems-locale-utils.js"])))=>i.map(i=>d[i]);
const __vitePreload = (fn) => fn();
import { jsxs, jsx } from "react/jsx-runtime";
import { useFloating, autoUpdate, offset, flip, shift, useDismiss, useRole, useClick, useInteractions, FloatingFocusManager } from "@floating-ui/react";
import { Calendar as Calendar$1 } from "@wix/wix-ui-icons-common/on-stage";
import { TextField, TextFieldTheme, IconButton, TextButton, TextButtonPriority } from "@wix/wix-ui-tpa/cssVars";
import classNames from "classnames";
import { useRef, useState, useCallback, Suspense, lazy } from "react";
import { useConfig } from "../../../contexts/config-context.js";
import { useFocusFieldEvent } from "../../../contexts/events-context.js";
import { useForcedState, ForcedState } from "../../../contexts/forced-state-context.js";
import { useTranslation } from "../../../contexts/translation-context.js";
import { classes } from "./date-picker-input-wut.st.css";
import { getLocalizedValue } from "./date-picker-utils.js";
const Calendar = lazy(() => __vitePreload(() => import("../calendar/calendar-wut.js"), true ? __vite__mapDeps([0,1,2,3,4,5,6,7,8,9]) : void 0));
const DatePickerInputWUT = ({
  inputId,
  ariaDescribedBy,
  label,
  placeholder,
  required,
  value,
  onChange,
  onFocus,
  onBlur,
  hasError,
  validation,
  firstDayOfWeek,
  availability,
  disabled,
  fieldId
}) => {
  const { t } = useTranslation();
  const { regionalFormat, isMobile, isComposer, isPreview, isEditor } = useConfig();
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [autoFocus, setAutoFocus] = useState(false);
  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);
  const calendarIconRef = useRef(null);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (_, _event, reason) => {
      if (reason === "outside-press" || reason === "escape-key") {
        closeCalendar();
      } else {
        openCalendar();
      }
    },
    middleware: [
      offset(8),
      flip(),
      shift({
        crossAxis: isMobile && true
      })
    ],
    whileElementsMounted: autoUpdate,
    placement: "bottom-start"
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const click = useClick(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
    click
  ]);
  const focusCalendarIcon = () => {
    calendarIconRef.current?.focus();
  };
  const openCalendar = () => {
    if (!isOpen && !disabled) {
      setIsOpen(true);
    }
  };
  const closeCalendar = () => {
    setAutoFocus(false);
    setIsOpen(false);
    focusInput();
    scrollInputIntoView();
  };
  const onInputClearButtonClicked = () => {
    onChange(null);
    focusCalendarIcon();
  };
  const handleBlur = (ev) => {
    if (!ev.relatedTarget?.closest('[data-hook="date-picker-calendar"]')) {
      onBlur();
    }
  };
  const scrollInputIntoView = () => {
    inputRef.current?.scrollIntoView?.({
      block: "center",
      inline: "nearest"
    });
  };
  useFocusFieldEvent(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, fieldId);
  const handleKeyDown = (event) => {
    switch (event.key) {
      case "Tab":
      case "Escape": {
        if (isOpen) {
          closeCalendar();
        }
        onBlur();
        return;
      }
      case " ":
      case "Enter": {
        if (!isOpen && !disabled) {
          openCalendar();
        }
        return;
      }
      case "Backspace": {
        onChange(null);
        if (isOpen) {
          closeCalendar();
        }
        return;
      }
      case "ArrowUp":
      case "ArrowDown":
      case "ArrowLeft":
      case "ArrowRight": {
        if (isOpen) {
          event.preventDefault();
          setAutoFocus(true);
        }
        return;
      }
      default:
        return;
    }
  };
  const ariaLabel = value ? `${value} ${label}` : label;
  const mobileMode = isMobile && !isComposer && !isPreview && !isEditor;
  const handleRef = (ref) => {
    inputRef.current = ref;
  };
  return /* @__PURE__ */ jsxs("div", { className: classes.wrapper, children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: refs.setReference,
        "data-hook": "popover-element",
        ...getReferenceProps(),
        children: /* @__PURE__ */ jsx(
          TextField,
          {
            "data-hook": "date-picker-input",
            id: inputId,
            inputRef: handleRef,
            value: value ? getLocalizedValue(value, regionalFormat) : "",
            placeholder,
            className: classNames(classes.datePickerInput, classes.theme, {
              [classes.error]: hasError,
              [classes.hover]: forcedHover,
              [classes.calendarIconDisabled]: disabled
            }),
            prefix: /* @__PURE__ */ jsx(
              IconButton,
              {
                icon: /* @__PURE__ */ jsx(Calendar$1, {}),
                onClick: openCalendar,
                "data-hook": "date-picker-calendar-icon",
                "aria-roledescription": t(
                  "date-picker.a11y.calendar-button.role-description"
                ),
                "aria-label": ariaLabel,
                "aria-describedby": ariaDescribedBy,
                "aria-invalid": hasError,
                "aria-selected": Boolean(value),
                innerRef: calendarIconRef,
                onKeyDown: handleKeyDown
              }
            ),
            disabled,
            error: hasError,
            newErrorMessage: true,
            onClick: openCalendar,
            onKeyDown: handleKeyDown,
            onClear: onInputClearButtonClicked,
            withClearButton: true,
            clearButtonAriaLabel: `${value} ${label} ${t(
              "date-picker.a11y.clear-button"
            )}`,
            inputMode: "none",
            readOnly: true,
            theme: TextFieldTheme.Box,
            required,
            onBlur: handleBlur,
            onFocus,
            tabIndex: -1,
            "aria-hidden": true
          }
        )
      }
    ),
    isOpen && /* @__PURE__ */ jsx(FloatingFocusManager, { context, disabled: true, children: /* @__PURE__ */ jsx(
      "div",
      {
        ref: refs.setFloating,
        style: { ...floatingStyles, zIndex: 1e3 },
        className: classNames(classes.content, {
          [classes.mobilePopoverContent]: mobileMode
        }),
        ...getFloatingProps(),
        children: /* @__PURE__ */ jsxs(Suspense, { fallback: /* @__PURE__ */ jsx("div", { className: classes.calendarSkeleton }), children: [
          /* @__PURE__ */ jsx(
            Calendar,
            {
              value,
              validation,
              firstDayOfWeek,
              availability,
              onChange,
              closeCalendar,
              focusInput,
              autoFocus
            }
          ),
          mobileMode && /* @__PURE__ */ jsx("div", { className: classes.closeIconContainer, children: /* @__PURE__ */ jsx(
            TextButton,
            {
              onClick: closeCalendar,
              priority: TextButtonPriority.primary,
              children: t("date-picker.calendar.close-button")
            }
          ) })
        ] })
      }
    ) })
  ] });
};
export {
  DatePickerInputWUT
};
//# sourceMappingURL=date-picker-input-wut.js.map
